// frontend/src/pages/Register.tsx
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { registerUser, type RegisterResponse } from "@/services/auth";
import { DEFAULT_LANGUAGE, normalizeLanguage, type Language } from "@/locales/locales";
import i18n, { LOCAL_STORAGE_LANG_KEY } from "@/i18n";
import { showToast } from "../utils/toast";
import Button from "@/components/ui/Button";
import ButtonLink from "@/components/ui/ButtonLink";

export default function Register() {
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [email,     setEmail]     = useState("");
  const [password,  setPassword]  = useState("");

  const [loading,   setLoading]   = useState(false);
  const [ready,     setReady]     = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { t } = useTranslation();

  // Turnstile widget container + state
  const widgetElRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | number | null>(null);
  const tokenRef    = useRef<string | null>(null);

  useEffect(() => {
    const sitekey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
    if (!sitekey) {
      console.error("[Register] Missing VITE_TURNSTILE_SITE_KEY env.");
      return;
    }

    const tryRender = () => {
      if (!window.turnstile?.render) return false;
      const el = widgetElRef.current;
      if (!el) return false;

      widgetIdRef.current = window.turnstile.render(el, {
        sitekey,
        size: "normal",
        appearance: "always",
        callback: (token: string) => { tokenRef.current = token; },
        "error-callback":   () => { tokenRef.current = null; },
        "timeout-callback": () => { tokenRef.current = null; },
        "expired-callback": () => { tokenRef.current = null; },
      });

      return true;
    };

    const id = window.setInterval(() => {
      if (tryRender()) {
        setReady(true);
        window.clearInterval(id);
      }
    }, 150);

    return () => window.clearInterval(id);
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const wid = widgetIdRef.current!;
      const latest =
        (window.turnstile?.getResponse?.(wid) as string | null | undefined) ?? tokenRef.current;

      if (!latest) {
        showToast(t("auth.register.error_verification"), "error");
        return;
      }

      // Client-side email check (prevents a pointless round-trip)
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        showToast(t("auth.register.error_email"), "error");
        return;
      }

      const fromNav = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2) : undefined;
      const preferred: Language = normalizeLanguage(fromNav) ?? DEFAULT_LANGUAGE;

      const data: RegisterResponse = await registerUser({
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        email:      email.trim(),
        password,
        turnstile_token: latest,
        language: preferred,
      });

      const { status, message, verification_email_sent: verOk } = data;

      // Optional: keep UI consistent immediately after register
      await i18n.changeLanguage(preferred);
      try {
        document.documentElement.setAttribute("lang", preferred);
        localStorage.setItem(LOCAL_STORAGE_LANG_KEY, preferred);
      } catch { /* ignore storage errors */ }

      if (status === "created") {
        setSubmitted(true);
        showToast(message || t("auth.register.success_created"), "success");
        if (!verOk) {
          showToast(t("auth.register.warning_email"), "warning");
        }
        setFirstName(""); setLastName(""); setEmail(""); setPassword("");
        return;
      }

      if (status === "existing_unverified") {
        showToast(message || t("auth.register.info_unverified"), "info");
        return;
      }

      if (status === "existing_verified") {
        showToast(message || t("auth.register.info_verified"), "info");
        return;
      }

      // Fallback (unexpected but non-error 2xx)
      setSubmitted(true);
      showToast(t("auth.register.success_created"), "success");
      setFirstName(""); setLastName(""); setEmail(""); setPassword("");

    } catch (e: any) {
      const resp = e?.response;
      const detail = resp?.data?.detail || resp?.data?.message;
      const http = resp?.status;

      if (!resp) {
        // Axios "Network Error" → make it human-friendly
        showToast(t("auth.register.error_network"), "error");
      } else if (http === 429) {
        showToast(t("auth.register.error_rate_limit"), "error");
      } else if (http && http >= 500) {
        showToast(t("auth.register.error_server"), "error");
      } else {
        showToast(String(detail || t("auth.register.error_generic")), "error");
      }

    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-50 pt-12 pb-12 md:pb-16 px-4">
      <div className="mx-auto w-full max-w-md bg-white shadow rounded px-6 py-6">
        <h1 className="text-xl font-semibold mb-4">{t("auth.register.create_account")}</h1>

        {submitted ? (
          <>
            <p className="text-sm text-gray-700">
              {t("auth.register.check_email")}
            </p>
            <div className="mt-4">
              <ButtonLink to="/login" variant="blue">{t("auth.register.go_to_login")}</ButtonLink>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block text-sm font-medium mb-1">{t("auth.register.first_name")}</label>
            <input
              className="standard-form-field"
              value={firstName}
              onChange={(e)=>setFirstName(e.target.value)}
              required
              autoComplete="given-name"
            />

            <label className="block text-sm font-medium mb-1">{t("auth.register.last_name")}</label>
            <input
              className="standard-form-field"
              value={lastName}
              onChange={(e)=>setLastName(e.target.value)}
              required
              autoComplete="family-name"
            />

            <label className="block text-sm font-medium mb-1">{t("auth.register.email")}</label>
            <input
              className="standard-form-field"
              type="email"
              value={email}
              onChange={(e)=>setEmail(e.target.value)}
              required
              autoComplete="email"
            />

            <label className="block text-sm font-medium mb-1">{t("auth.register.password")}</label>
            <input
              className="standard-form-field"
              type="password"
              value={password}
              onChange={(e)=>setPassword(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
            />

            <Button
              type="submit"
              variant="blue"
              loading={loading}
              loadingText={t("common.creating")}
              disabled={!ready}
              className="w-full"
            >
              {t("auth.register.button")}
            </Button>

            <div className="text-xs text-gray-500 text-center mt-4">
              <Trans
                i18nKey="auth.register.consent"
                components={{
                  tos: <Link to="/terms" className="hover:underline" />,
                  privacy: <Link to="/privacy" className="hover:underline" />
                }}
                values={{
                  tosLabel: t("auth.register.terms"),
                  privacyLabel: t("auth.register.privacy")
                }}
              />
            </div>

            {/* Visible Turnstile widget container */}
            <div className="mt-4 min-h-16 md:min-h-20 relative">
              <div
                ref={widgetElRef}
                aria-label={t("auth.register.captcha")}
                aria-busy={!ready}
              />
            </div>
            
          </form>
        )}
      </div>
    </div>
  );
}
